import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const LoginForm: React.FC = () => {
  const { login, isAuthenticated, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Marcado por padrão
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemConfig, setSystemConfig] = useState<any>({
    company_logo: '',
    company_name: 'Lexpress Sistema'
  });

  useEffect(() => {
    // Carregar configuração do sistema
    try {
      const savedConfig = localStorage.getItem('system_config');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        setSystemConfig(config);
      }
    } catch (err) {
      console.error('Erro ao carregar configuração:', err);
    }
  }, []);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    // Limpar tokens antigos antes do novo login
    localStorage.removeItem('authTokens');
    
    setIsSubmitting(true);
    setError('');

    try {
      await login(formData.username, formData.password);
    } catch (err: any) {
      setError(
        err.response?.data?.detail || 
        'Erro ao fazer login. Verifique suas credenciais.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-secondary-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          {systemConfig.company_logo && (
            <div className="mb-6">
              <img
                src={systemConfig.company_logo}
                alt={systemConfig.company_name || 'Logo'}
                className="h-16 w-auto mx-auto object-contain"
              />
            </div>
          )}
          <h1 className="text-3xl font-bold text-primary-600 mb-2">
            {systemConfig.company_name || 'Lexpress Sistema'}
          </h1>
          <h2 className="text-sm font-bold text-secondary-900 mb-6">
            Faça login em sua conta
          </h2>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-secondary-700 mb-2">
              Usuário
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              className="input-field"
              placeholder="Digite seu usuário"
              value={formData.username}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-2">
              Senha
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="input-field pr-10"
                placeholder="Digite sua senha"
                value={formData.password}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4 text-secondary-400" />
                ) : (
                  <EyeIcon className="h-4 w-4 text-secondary-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-secondary-700 cursor-pointer"
              >
                Manter-me conectado
              </label>
            </div>

            <div className="text-sm">
              <span className="text-secondary-500">
                {rememberMe ? '(30 dias)' : '(8 horas)'}
              </span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Entrando...
              </div>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <p className="text-xs text-secondary-600 mb-3">
            Não tem uma conta?
          </p>
          <Link
            to="/register"
            className="w-full py-3 px-4 border border-primary-600 text-sm font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 inline-flex items-center justify-center"
          >
            Criar nova conta
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;